import { format } from 'date-fns';

import { formatNumber } from '@/localization/numberFormat';

export const round = (value?: number, decimals: number = 0) => {
  if (value === undefined) return undefined;
  const scalingFactor = Math.pow(10, decimals);
  return Math.round((value + Number.EPSILON) * scalingFactor) / scalingFactor;
};

export const roundToTwoDecimals = (value: number) => round(value, 2);

export const formatDurationFromSeconds = (totalSeconds: number | undefined) => {
  if (totalSeconds === undefined || totalSeconds === null) {
    return '-';
  }

  const weeks = Math.floor(totalSeconds / 604800);
  const days = Math.floor((totalSeconds % 604800) / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  const parts: string[] = [];

  if (weeks) parts.push(`${weeks}w`);
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);

  // If all fields above are zero, show seconds
  if (!parts.length) parts.push(`${seconds}s`);

  return parts.join(' ');
};

export const toOptionalNumber = (val: string | number | null | undefined): number | undefined => {
  if (val == null || (typeof val === 'string' && val === '')) return undefined;
  return Number(val);
};

export const formatLocaleInteger = (value: number): string => formatNumber(value);

export const formatLocalePercent = (
  locale: string,
  fraction: number,
  options?: Pick<Intl.NumberFormatOptions, 'minimumFractionDigits' | 'maximumFractionDigits'>
): string =>
  new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
    ...options,
  }).format(fraction);

export const formatTimestamp = (val: string | object): string => {
  // Handle non-string inputs
  if (typeof val !== 'string' || !val.trim()) {
    return '-';
  }

  // Validate that the string can be parsed as a date
  const date = new Date(val);
  if (isNaN(date.getTime())) {
    return '-';
  }

  try {
    return format(date, 'yyyy-MM-dd HH:mm:ss');
  } catch {
    // Fallback if date-fns format fails
    return '-';
  }
};

export const formatGToKg = (grams: number | undefined | null) => {
  if (grams === undefined || grams === null) {
    return '-';
  }

  const kg = grams / 1000;
  return formatNumber(kg, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

export const formatGPerSecondToKgPerMinute = (gramsPerSecond: number | undefined | null) => {
  if (gramsPerSecond === undefined || gramsPerSecond === null) {
    return '-';
  }

  const kgPerMinute = (gramsPerSecond * 60) / 1000;
  return formatNumber(kgPerMinute, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};
